/**
 * Opens a new window/tab with the given HTML for printing.
 *
 * Important: Do not use the `noopener` window feature — in modern browsers
 * `window.open(..., 'noopener')` returns `null`, so the opener cannot interact
 * with the new tab. Using a blob: URL gives a real Window reference.
 *
 * @param {string} html Full HTML document string
 * @param {{
 *   autoPrint?: boolean,
 *   showToolbar?: boolean,
 *   closeAfterPrint?: boolean,
 * }} [options]
 *   - autoPrint: open the system print dialog automatically (default true for legacy callers)
 *   - showToolbar: inject Print / Close toolbar (default: !autoPrint)
 *   - closeAfterPrint: close the tab after print dialog finishes (default: showToolbar)
 * @returns {boolean} true if a window was opened
 */
export function openPrintWindow(html, options = {}) {
  if (html == null || typeof html !== "string" || html.trim() === "") {
    return false;
  }

  const autoPrint = options.autoPrint !== false;
  const showToolbar =
    options.showToolbar != null ? Boolean(options.showToolbar) : !autoPrint;
  const closeAfterPrint =
    options.closeAfterPrint != null
      ? Boolean(options.closeAfterPrint)
      : showToolbar;

  const preparedHtml = showToolbar
    ? injectPrintPreviewChrome(html, { closeAfterPrint })
    : html;

  const blob = new Blob([preparedHtml], { type: "text/html;charset=utf-8" });
  const blobUrl = URL.createObjectURL(blob);

  const printWindow = window.open(blobUrl, "_blank");

  if (!printWindow) {
    URL.revokeObjectURL(blobUrl);
    return false;
  }

  const cleanup = () => {
    setTimeout(() => URL.revokeObjectURL(blobUrl), 120_000);
  };

  if (!autoPrint) {
    cleanup();
    return true;
  }

  let printed = false;
  const runPrintOnce = () => {
    if (printed) return;
    printed = true;
    try {
      printWindow.focus();
      printWindow.print();
    } catch {
      // ignore
    }
    cleanup();
  };

  printWindow.addEventListener("load", () => setTimeout(runPrintOnce, 200), {
    once: true,
  });

  setTimeout(() => {
    if (!printed) runPrintOnce();
  }, 1200);

  return true;
}

/**
 * Inject a screen-only Print / Close toolbar into a full HTML document.
 * Print dialog is opened only when the user clicks Print.
 *
 * @param {string} html
 * @param {{ closeAfterPrint?: boolean }} opts
 */
function injectPrintPreviewChrome(html, opts = {}) {
  const closeAfterPrint = opts.closeAfterPrint !== false;
  const chromeStyle = `
<style id="kv-print-preview-chrome-style">
  .kv-print-toolbar {
    position: sticky;
    top: 0;
    z-index: 99999;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 10px;
    padding: 12px 16px;
    background: #0f172a;
    color: #fff;
    font-family: system-ui, -apple-system, Segoe UI, Roboto, sans-serif;
    box-shadow: 0 2px 10px rgba(15, 23, 42, 0.25);
  }
  .kv-print-toolbar__hint {
    margin-right: auto;
    font-size: 13px;
    color: #cbd5e1;
  }
  .kv-print-toolbar button {
    appearance: none;
    border: 0;
    border-radius: 8px;
    padding: 8px 14px;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
  }
  .kv-print-toolbar__print {
    background: #2563eb;
    color: #fff;
  }
  .kv-print-toolbar__print:hover { background: #1d4ed8; }
  .kv-print-toolbar__close {
    background: #e2e8f0;
    color: #0f172a;
  }
  .kv-print-toolbar__close:hover { background: #cbd5e1; }
  @media print {
    .kv-print-toolbar { display: none !important; }
  }
</style>`;

  const chromeBar = `
<div class="kv-print-toolbar" role="toolbar" aria-label="Print actions">
  <span class="kv-print-toolbar__hint">Review the document, then click Print.</span>
  <button type="button" class="kv-print-toolbar__print" id="kv-print-btn">Print</button>
  <button type="button" class="kv-print-toolbar__close" id="kv-close-btn">Close</button>
</div>`;

  const chromeScript = `
<script id="kv-print-preview-chrome-script">
(function () {
  function closeTab() {
    try { window.close(); } catch (e) {}
    // If the browser blocks window.close(), navigate away as a fallback.
    try { window.location.href = "about:blank"; } catch (e2) {}
  }
  var printBtn = document.getElementById("kv-print-btn");
  var closeBtn = document.getElementById("kv-close-btn");
  if (printBtn) {
    printBtn.addEventListener("click", function () {
      try { window.focus(); window.print(); } catch (e) {}
    });
  }
  if (closeBtn) {
    closeBtn.addEventListener("click", closeTab);
  }
  ${
    closeAfterPrint
      ? `window.addEventListener("afterprint", function () { closeTab(); });`
      : ""
  }
})();
</script>`;

  let out = String(html);

  if (/<\/head>/i.test(out)) {
    out = out.replace(/<\/head>/i, `${chromeStyle}</head>`);
  } else if (/<html[^>]*>/i.test(out)) {
    out = out.replace(/<html[^>]*>/i, (m) => `${m}<head>${chromeStyle}</head>`);
  } else {
    out = `<!DOCTYPE html><html><head>${chromeStyle}</head><body>${out}</body></html>`;
  }

  if (/<body[^>]*>/i.test(out)) {
    out = out.replace(/<body([^>]*)>/i, `<body$1>${chromeBar}`);
  } else {
    out = out.replace(/<\/head>/i, `</head><body>${chromeBar}`);
    if (!/<\/body>/i.test(out)) out += "</body></html>";
  }

  if (/<\/body>/i.test(out)) {
    out = out.replace(/<\/body>/i, `${chromeScript}</body>`);
  } else {
    out += chromeScript;
  }

  return out;
}
